import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Home as HomeIcon, Lock, Eye, EyeOff, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { api } from '../lib/api';
import { FormError, FormBanner } from '../components/FormError';
import useFormValidation from '../hooks/useFormValidation';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const v = useFormValidation();

  const submit = async (e) => {
    e.preventDefault();
    v.reset();
    const ok = v.validate({
      password: !pwd ? 'La contraseña es obligatoria.'
        : pwd.length < 6 ? 'La contraseña debe tener al menos 6 caracteres.'
        : null,
      password_confirm: pwd && pwd !== pwd2 ? 'Las contraseñas no coinciden.' : null,
    });
    if (!ok) return;
    setLoading(true);
    try {
      await api.resetPassword(token, pwd);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      v.applyError(err, 'No pudimos actualizar tu contraseña. El enlace puede haber expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-habita-cream flex flex-col p-6 lg:p-12">
      <SEO title="Restablecer contraseña" noindex />
      <Link to="/" className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-md bg-habita flex items-center justify-center"><HomeIcon className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
        <span className="font-serif font-bold text-2xl text-habita">habita<span className="text-amber-500">.</span><span className="text-gray-800">co</span></span>
      </Link>

      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 lg:p-10 border border-gray-100 shadow-sm">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-habita-mint mx-auto flex items-center justify-center"><Check className="w-8 h-8 text-habita" strokeWidth={3} /></div>
              <h1 className="font-serif text-3xl font-bold text-habita-dark mt-5">¡Contraseña actualizada!</h1>
              <p className="text-gray-600 mt-3">Ya puedes iniciar sesión con tu nueva contraseña.</p>
              <p className="text-xs text-gray-500 mt-2">Redirigiendo...</p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark">Crear nueva contraseña</h1>
              <p className="text-gray-600 mt-3">Elige una contraseña segura.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
                <FormBanner msg={v.banner} testid="reset-error-banner" />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Nueva contraseña</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                      type={show ? 'text' : 'password'}
                      value={pwd}
                      onChange={e => { setPwd(e.target.value); v.clearField('password'); }}
                      className={`h-12 pl-10 pr-12 ${v.errorClass('password')}`}
                      placeholder="Mínimo 6 caracteres"
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                      {...v.field('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShow(!show)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full text-gray-500 hover:text-habita hover:bg-habita-mint transition-colors z-10"
                      aria-label={show ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
                      aria-pressed={show}
                      tabIndex={-1}
                      data-testid="reset-toggle-password"
                    >
                      {show ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                    </button>
                  </div>
                  <FormError msg={v.fieldErrors.password} testid="reset-error-password" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Repite la contraseña</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                      type={show ? 'text' : 'password'}
                      value={pwd2}
                      onChange={e => { setPwd2(e.target.value); v.clearField('password_confirm'); }}
                      className={`h-12 pl-10 pr-4 ${v.errorClass('password_confirm')}`}
                      autoComplete="new-password"
                      data-testid="reset-password-confirm-input"
                      {...v.field('password_confirm')}
                    />
                  </div>
                  <FormError msg={v.fieldErrors.password_confirm} testid="reset-error-confirm" />
                </div>
                <Button type="submit" disabled={loading} className="btn-habita w-full h-12 rounded-full font-semibold disabled:opacity-60" data-testid="reset-submit-btn">
                  {loading ? 'Actualizando...' : 'Actualizar contraseña'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
